import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiChevronRight, FiFilter, FiX } from 'react-icons/fi';
import API from '../../utils/api';
import ProductCard from '../../components/common/ProductCard';
import '../ProductsPage.css';
import './CatalogPages.css';

const SkeletonCard = () => (
  <div className="product-card skeleton-card">
    <div className="skeleton-img" />
    <div className="product-card-body">
      <div className="skeleton-line short" /><div className="skeleton-line" /><div className="skeleton-line medium" />
    </div>
  </div>
);

export default function SubcategoryPage() {
  const { categorySlug, subcategorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [subcategory, setSubcategory] = useState(null);
  const [products,    setProducts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [subLoading,  setSubLoading]  = useState(true);
  const [notFound,    setNotFound]    = useState(false);
  const [totalCount,  setTotalCount]  = useState(0);
  const [totalPages,  setTotalPages]  = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1');

  // Load subcategory
  useEffect(() => {
    setSubLoading(true);
    API.get(`/catalog/public/categories/${categorySlug}/subcategories/${subcategorySlug}`)
      .then(r => setSubcategory(r.data.subcategory))
      .catch(() => setNotFound(true))
      .finally(() => setSubLoading(false));
  }, [categorySlug, subcategorySlug]);

  // Load products
  const loadProducts = useCallback(() => {
    if (!subcategory) return;
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12, sort, status: 'active', subcategoryId: subcategory.id });
    API.get(`/catalog/public/products?${params}`)
      .then(r => {
        setProducts(r.data.products || []);
        setTotalCount(r.data.count  || 0);
        setTotalPages(r.data.pages  || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subcategory, page, sort]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const updateParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  if (notFound) return (
    <div style={{ padding: '120px 0', textAlign: 'center' }}>
      <h2>Not found</h2>
      <Link to="/products" className="btn btn-primary" style={{ marginTop: 16 }}>Back to Products</Link>
    </div>
  );

  const category = subcategory?.category;

  return (
    <>
      <Helmet>
        <title>{subcategory?.seoTitle || `${subcategory?.name || ''} | Angel Printers`}</title>
        <meta name="description" content={subcategory?.seoDescription || subcategory?.description || ''} />
        {subcategory && <link rel="canonical" href={`${window.location.origin}/category/${categorySlug}/${subcategorySlug}`} />}
      </Helmet>

      <div className="products-page">
        <div className="products-header">
          <div className="container">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link><FiChevronRight size={13} />
              {category && <><Link to={`/${categorySlug}`}>{category.name}</Link><FiChevronRight size={13} /></>}
              <span>{subcategory?.name || '…'}</span>
            </nav>
            {subcategory?.image && (
              <div className="cat-banner"><img src={subcategory.image} alt={subcategory.name} /></div>
            )}
            <h1>{subLoading ? '…' : subcategory?.name}</h1>
            {subcategory?.description && <p className="cat-header-desc">{subcategory.description}</p>}
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', marginTop: 4 }}>
              {totalCount} product{totalCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="container products-layout">
          {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
          <aside className={`products-sidebar ${sidebarOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
              <h3>Sort</h3>
              <button className="icon-btn" onClick={() => setSidebarOpen(false)}><FiX size={20} /></button>
            </div>
            <div className="sidebar-section">
              <h4>Sort By</h4>
              <select className="form-control" value={sort} onChange={e => updateParam('sort', e.target.value)}>
                <option value="newest">Newest</option>
                <option value="name">Name A–Z</option>
                <option value="price">Price: Low–High</option>
              </select>
            </div>
          </aside>

          <main className="products-main">
            <div className="products-toolbar">
              <button className="btn btn-outline btn-sm filter-toggle" onClick={() => setSidebarOpen(true)}>
                <FiFilter size={15} /> Sort
              </button>
              <span className="products-count">{totalCount} product{totalCount !== 1 ? 's' : ''}</span>
              <div style={{ flex: 1 }} />
              <select className="form-control toolbar-sort" value={sort} onChange={e => updateParam('sort', e.target.value)}>
                <option value="newest">Newest</option>
                <option value="name">Name A–Z</option>
                <option value="price">Price ↑</option>
              </select>
            </div>

            {loading ? (
              <div className="products-grid">
                {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : products.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">📦</div>
                <h3>No products yet</h3>
                <p>Check back soon.</p>
                <Link to={`/${categorySlug}`} className="btn btn-primary">Back to {category?.name}</Link>
              </div>
            ) : (
              <div className="products-grid">
                {products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}

            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" disabled={page <= 1} onClick={() => updateParam('page', page - 1)}>← Prev</button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page + i - 3;
                  if (p < 1 || p > totalPages) return null;
                  return <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => updateParam('page', p)}>{p}</button>;
                })}
                <button className="page-btn" disabled={page >= totalPages} onClick={() => updateParam('page', page + 1)}>Next →</button>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
}